<template>
  <ContentWrap>
    <el-form :inline="true" class="-mb-15px">
      <el-form-item>
        <el-button v-hasPermi="['market:board:create']" type="primary" @click="openForm()">
          <Icon icon="ep:plus" />新增板块
        </el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="编码" prop="code" width="140" show-overflow-tooltip />
      <el-table-column label="标题" prop="title" min-width="160" />
      <el-table-column label="类型" width="140">
        <template #default="{ row }">
          <span>{{ boardTypeLabel(row.boardType) }}</span>
          <span v-if="row.boardType" class="text-gray-400 text-12px ml-4px">({{ row.boardType }})</span>
        </template>
      </el-table-column>
      <el-table-column label="排序" prop="boardSort" width="80" />
      <el-table-column label="条数上限" prop="limitCount" width="90" />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-switch
            :model-value="row.status"
            :active-value="0"
            :inactive-value="1"
            @change="(v) => changeStatus(row, Number(v))"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button v-hasPermi="['market:board:update']" link type="primary" @click="openForm(row)">
            编辑
          </el-button>
          <el-button v-hasPermi="['market:board:delete']" link type="danger" @click="handleDelete(row.id)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog v-model="formVisible" :title="formData.id ? '编辑板块' : '新增板块'" width="520px">
    <el-form :model="formData" label-width="90px">
      <el-form-item label="编码"><el-input v-model="formData.code" /></el-form-item>
      <el-form-item label="标题"><el-input v-model="formData.title" /></el-form-item>
      <el-form-item label="类型" required>
        <el-select v-model="formData.boardType" class="!w-full" placeholder="请选择板块类型">
          <el-option
            v-for="item in boardTypeOptions"
            :key="item.typeCode"
            :label="`${item.name} (${item.typeCode})`"
            :value="item.typeCode"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="关联Key">
        <el-input-number v-model="formData.refKey" class="!w-full" />
        <div v-if="selectedTypeHint" class="text-12px text-gray-400 mt-4px">{{ selectedTypeHint }}</div>
      </el-form-item>
      <el-form-item label="排序"><el-input-number v-model="formData.boardSort" class="!w-full" /></el-form-item>
      <el-form-item label="条数上限"><el-input-number v-model="formData.limitCount" class="!w-full" /></el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="formVisible = false">取消</el-button>
      <el-button type="primary" @click="submitForm">确定</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import * as BoardApi from '@/api/market/board'
import { getMarketBoardTypeEnabledList } from '@/api/market/dict'
import type { MarketBoardVO } from '@/api/market/board'

defineOptions({ name: 'MarketBoard' })

interface BoardTypeOption {
  typeCode?: string
  name?: string
  refHint?: string
}

const message = useMessage()
const loading = ref(false)
const list = ref<MarketBoardVO[]>([])
const formVisible = ref(false)
const formData = ref<MarketBoardVO>({ status: 0, boardSort: 0, limitCount: 10 })
const boardTypeOptions = ref<BoardTypeOption[]>([])
const boardTypeMap = computed(() => {
  const map = new Map<string, BoardTypeOption>()
  boardTypeOptions.value.forEach((item) => {
    if (item.typeCode) map.set(item.typeCode, item)
  })
  return map
})

const selectedTypeHint = computed(
  () => boardTypeMap.value.get(formData.value.boardType || '')?.refHint
)

const boardTypeLabel = (code?: string) => {
  if (!code) return '-'
  return boardTypeMap.value.get(code)?.name || code
}

const loadBoardTypes = async () => {
  boardTypeOptions.value = await getMarketBoardTypeEnabledList()
}

const load = async () => {
  loading.value = true
  try {
    list.value = await BoardApi.getMarketBoardList()
  } catch (e: any) {
    message.error(e?.message || '加载板块失败')
  } finally {
    loading.value = false
  }
}

const openForm = (row?: MarketBoardVO) => {
  formData.value = row ? { ...row } : { status: 0, boardSort: 0, limitCount: 10 }
  formVisible.value = true
}

const submitForm = async () => {
  if (!formData.value.boardType) {
    message.warning('请选择板块类型')
    return
  }
  if (formData.value.id) {
    await BoardApi.updateMarketBoard(formData.value)
  } else {
    await BoardApi.createMarketBoard(formData.value)
  }
  message.success('已保存')
  formVisible.value = false
  await load()
}

const changeStatus = async (row: MarketBoardVO, status: number) => {
  await BoardApi.updateMarketBoardStatus(row.id!, status)
  row.status = status
}

const handleDelete = async (id: number) => {
  await message.delConfirm()
  await BoardApi.deleteMarketBoard(id)
  message.success('已删除')
  await load()
}

onMounted(async () => {
  await loadBoardTypes()
  await load()
})
</script>
